/** Popup → opener: OAuth completed; includes tokens for sandbox-safe handoff. */
export const EHR_OAUTH_HANDOFF_MESSAGE = 'tempus:ehr-oauth-handoff';
/** Opener → popup: tokens were applied; popup may close. */
export const EHR_OAUTH_HANDOFF_ACK_MESSAGE = 'tempus:ehr-oauth-handoff-ack';

export const HANDOFF_ACK_TIMEOUT_MS = 10000;
/**
 * Re-post the handoff to the opener on an interval until it acks. The embedded iframe listener may
 * not be mounted yet when a fast (existing-session) SSO returns, so a single post can be missed —
 * which left the user stranded in the popup ~50% of the time. Retrying closes that race without
 * registering the listener before React mounts.
 */
export const HANDOFF_RETRY_INTERVAL_MS = 400;

/**
 * Wait until okta-auth-js reports an authenticated session (e.g. after setTokens).
 * Avoids navigating to SecureRoute before @okta/okta-react authState has caught up.
 */
export function waitForOktaAuthenticated(oktaAuth, timeoutMs = HANDOFF_ACK_TIMEOUT_MS) {
  const current = oktaAuth?.authStateManager?.getAuthState?.();
  if (current?.isAuthenticated) {
    return Promise.resolve(current);
  }

  return new Promise((resolve, reject) => {
    let timer;
    const unsubscribe = oktaAuth.authStateManager.subscribe((authState) => {
      if (authState?.isAuthenticated) {
        clearTimeout(timer);
        unsubscribe();
        resolve(authState);
      }
    });
    timer = setTimeout(() => {
      unsubscribe();
      reject(new Error('Timed out waiting for Okta auth state'));
    }, timeoutMs);
  });
}

/**
 * Close the SSO popup once the embedded iframe has taken over the session.
 *
 * The popup was opened via window.open, so it is allowed to close itself. Do NOT navigate it to
 * about:blank first: navigating unloads the document and cancels any deferred close, leaving an
 * empty window stranded (the embedded iframe still gets the tokens, so the app is embedded, but the
 * popup never closes). A direct close is what reliably tears the popup down.
 */
export function closeEhrOauthPopupSafely(browsingWindow) {
  if (!browsingWindow) {
    return;
  }
  try {
    browsingWindow.close();
  } catch {
    // noop
  }
}

/**
 * After OAuth in a popup opened from an Epic iframe, hand tokens to the opener via postMessage.
 * Epic sandboxes the popup (no allow-popups-to-escape-sandbox), so opener.location.replace is
 * blocked even when window.opener is defined.
 *
 * @param {import('@okta/okta-auth-js').OktaAuth} oktaAuth
 * @param {string} [originalUri]
 * @param {Window} [browsingWindow]
 * @returns {Promise<boolean>}
 */
export async function handOffEhrOauthPopupToOpener(
  oktaAuth,
  originalUri,
  browsingWindow = typeof window !== 'undefined' ? window : undefined,
) {
  if (!browsingWindow?.opener || browsingWindow.opener.closed) {
    return false;
  }

  const tokens = await oktaAuth?.tokenManager?.getTokens?.();
  if (!tokens?.accessToken) {
    return handOffOauthPopupToOpenerByNavigation(originalUri, browsingWindow);
  }

  const target = originalUri || browsingWindow.location.href;
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const targetOrigin = browsingWindow.location.origin;
  const payload = {
    type: EHR_OAUTH_HANDOFF_MESSAGE,
    requestId,
    originalUri: target,
    tokens,
  };

  return new Promise((resolve) => {
    let settled = false;
    let timer;
    let retryInterval;

    const finish = (result) => {
      if (settled) {
        return;
      }
      settled = true;
      browsingWindow.removeEventListener('message', onAck);
      clearTimeout(timer);
      clearInterval(retryInterval);
      resolve(result);
    };

    const onAck = (event) => {
      if (event.origin !== targetOrigin) {
        return;
      }
      if (event.data?.type !== EHR_OAUTH_HANDOFF_ACK_MESSAGE) {
        return;
      }
      if (event.data?.requestId !== requestId) {
        return;
      }
      closeEhrOauthPopupSafely(browsingWindow);
      finish(true);
    };

    const postHandoff = () => {
      try {
        browsingWindow.opener.postMessage(payload, targetOrigin);
      } catch {
        finish(false);
      }
    };

    browsingWindow.addEventListener('message', onAck);
    postHandoff();
    retryInterval = setInterval(() => {
      if (!settled) {
        postHandoff();
      }
    }, HANDOFF_RETRY_INTERVAL_MS);

    timer = setTimeout(() => {
      finish(handOffOauthPopupToOpenerByNavigation(target, browsingWindow));
    }, HANDOFF_ACK_TIMEOUT_MS);
  });
}

/**
 * Legacy handoff when tokens are unavailable or postMessage is not acked in time.
 * Works for non-sandbox popups; fails under Epic sandbox (see postMessage path above).
 */
export function handOffOauthPopupToOpenerByNavigation(
  originalUri,
  browsingWindow = typeof window !== 'undefined' ? window : undefined,
) {
  if (!browsingWindow?.opener || browsingWindow.opener.closed) {
    return false;
  }
  try {
    const target = originalUri || browsingWindow.location.href;
    browsingWindow.opener.location.replace(target);
    browsingWindow.close();
    return true;
  } catch {
    return false;
  }
}

/** @deprecated Use handOffEhrOauthPopupToOpener when oktaAuth is available. */
export function handOffOauthPopupToOpener(
  originalUri,
  browsingWindow = typeof window !== 'undefined' ? window : undefined,
) {
  return handOffOauthPopupToOpenerByNavigation(originalUri, browsingWindow);
}

/**
 * Listen for OAuth tokens from the EHR SSO popup and complete login in the embedded iframe.
 *
 * @param {object} options
 * @param {import('@okta/okta-auth-js').OktaAuth} options.oktaAuth
 * @param {(payload: { originalUri: string, tokens: object }) => void | Promise<void>} [options.onHandoffComplete]
 * @param {Window} [options.listeningWindow]
 * @returns {() => void} unsubscribe
 */
export function registerEhrOauthHandoffListener({
  oktaAuth,
  onHandoffComplete,
  listeningWindow = typeof window !== 'undefined' ? window : undefined,
}) {
  if (!listeningWindow) {
    return () => {};
  }

  const expectedOrigin = listeningWindow.location.origin;
  // The popup retries the handoff until it is acked, so the same requestId can arrive multiple
  // times. Track in-flight and completed ids to apply tokens / navigate exactly once, and re-ack
  // duplicates so a retry that raced a slow first pass still lets the popup close.
  const processedRequestIds = new Set();
  const inFlightRequestIds = new Set();

  const ackPopup = (event, requestId) => {
    if (event.source && !event.source.closed) {
      event.source.postMessage({ type: EHR_OAUTH_HANDOFF_ACK_MESSAGE, requestId }, event.origin);
    }
  };

  const onMessage = async (event) => {
    if (event.origin !== expectedOrigin) {
      return;
    }
    if (event.data?.type !== EHR_OAUTH_HANDOFF_MESSAGE) {
      return;
    }
    if (!event.data?.tokens?.accessToken || !event.data?.requestId) {
      return;
    }

    const { requestId, originalUri, tokens } = event.data;

    if (processedRequestIds.has(requestId) || inFlightRequestIds.has(requestId)) {
      ackPopup(event, requestId);
      return;
    }

    inFlightRequestIds.add(requestId);
    try {
      await oktaAuth.tokenManager.setTokens(tokens);
      await oktaAuth.authStateManager.updateAuthState();

      // Ack immediately so the popup can close without waiting on iframe navigation.
      ackPopup(event, requestId);

      await waitForOktaAuthenticated(oktaAuth);
      if (onHandoffComplete) {
        await onHandoffComplete({ originalUri, tokens });
      }
      processedRequestIds.add(requestId);
    } catch {
      // Do not ack; popup retries until timeout.
    } finally {
      inFlightRequestIds.delete(requestId);
    }
  };

  listeningWindow.addEventListener('message', onMessage);
  return () => listeningWindow.removeEventListener('message', onMessage);
}
